/* Experience Baires — global.js
   Progressive enhancement only: every feature below has a no-JS fallback in Liquid.
   No dependencies. Motion lives in motion.js. */
(() => {
  'use strict';

  const EB = (window.EB = window.EB || {});
  const routes = EB.routes || {};
  const strings = EB.strings || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------ *
   * Analytics: custom events go to Shopify's Customer Events (pixels)
   * and to dataLayer when GTM is consented and loaded.
   * ------------------------------------------------------------------ */
  const track = (name, detail = {}) => {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, detail);
      }
    } catch (e) { /* noop */ }
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...detail });
  };
  EB.track = track;

  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-track]');
    if (!el) return;
    track(el.dataset.track, {
      location: el.dataset.trackLocation || '',
      label: (el.textContent || '').trim().slice(0, 80),
      url: el.getAttribute('href') || ''
    });
  });

  const trackSuccess = (root = document) => {
    $$('[data-track-success]', root).forEach((el) => {
      if (el.dataset.tracked) return;
      el.dataset.tracked = '1';
      track(el.dataset.trackSuccess, { location: el.dataset.trackLocation || '' });
    });
  };

  /* ------------------------------------------------------------------ *
   * Utilities
   * ------------------------------------------------------------------ */
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe, summary';

  const trapFocus = (container, returnTo) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => {
      container.removeEventListener('keydown', handler);
      if (returnTo && typeof returnTo.focus === 'function') returnTo.focus({ preventScroll: true });
    };
  };

  let scrollLocks = 0;
  const lockScroll = () => {
    scrollLocks += 1;
    if (scrollLocks === 1) {
      const sw = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar', `${sw}px`);
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${sw}px`;
    }
  };
  const unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  };

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');

  const toast = (message) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 3200);
  };
  EB.toast = toast;

  const formatMoney = (cents) => {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  /* Generic overlay controller for drawers / modals (all start with [hidden]). */
  const createOverlay = (el, { onOpen, onClose, initialFocus } = {}) => {
    let release = null;
    let opener = null;
    const isOpen = () => !el.hidden;
    const open = (trigger) => {
      if (isOpen()) return;
      opener = trigger || document.activeElement;
      el.hidden = false;
      lockScroll();
      requestAnimationFrame(() => {
        el.classList.add('is-open');
        const target = (initialFocus && $(initialFocus, el)) || $(FOCUSABLE, el);
        if (target) target.focus({ preventScroll: true });
      });
      release = trapFocus(el, opener);
      if (onOpen) onOpen();
    };
    const close = () => {
      if (!isOpen() || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      const done = () => {
        el.hidden = true;
        unlockScroll();
        if (release) release();
        release = null;
        if (onClose) onClose();
      };
      if (reducedMotion.matches) done();
      else setTimeout(done, 420);
    };
    on(el, 'keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } });
    return { open, close, isOpen, el };
  };

  /* ------------------------------------------------------------------ *
   * Header: hide on scroll down, solid after leaving the hero,
   * mega menus (details) with outside-click + Escape.
   * ------------------------------------------------------------------ */
  const initHeader = () => {
    const wrap = $('.section-header');
    const header = $('[data-header]');
    if (!wrap || !header) return;
    const sticky = header.dataset.sticky !== 'false';
    let lastY = window.scrollY;
    let ticking = false;

    const setHeight = () => {
      const h = header.getBoundingClientRect().height;
      if (h) document.documentElement.style.setProperty('--header-h', `${Math.round(h)}px`);
    };
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);

    const update = () => {
      const y = window.scrollY;
      wrap.classList.toggle('is-scrolled', y > 40);
      const menuOpen = $$('[data-nav-details][open]', header).length > 0;
      if (sticky && !menuOpen) {
        const hide = y > lastY && y > 320;
        wrap.classList.toggle('is-hidden', hide);
        document.body.classList.toggle('header-hidden', hide);
      }
      lastY = y;
      ticking = false;
    };
    on(window, 'scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();

    const details = $$('[data-nav-details]', header);
    details.forEach((d) => {
      on(d, 'toggle', () => {
        if (d.open) {
          details.forEach((o) => { if (o !== d) o.open = false; });
          wrap.classList.add('is-scrolled');
        } else {
          update();
        }
      });
      // Hover intent on fine pointers; click still works everywhere.
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        let t;
        on(d, 'mouseenter', () => { clearTimeout(t); t = setTimeout(() => { d.open = true; }, 80); });
        on(d, 'mouseleave', () => { clearTimeout(t); t = setTimeout(() => { d.open = false; }, 160); });
      }
    });
    on(document, 'click', (e) => {
      details.forEach((d) => { if (d.open && !d.contains(e.target)) d.open = false; });
    });
    on(document, 'keydown', (e) => {
      if (e.key !== 'Escape') return;
      details.forEach((d) => {
        if (d.open) { d.open = false; $('summary', d).focus(); }
      });
    });
  };

  /* Mobile menu drawer */
  const initMenuDrawer = () => {
    const drawer = $('[data-menu-drawer]');
    if (!drawer) return;
    const toggles = $$('[data-menu-open]');
    const overlay = createOverlay(drawer, {
      onOpen: () => toggles.forEach((b) => b.setAttribute('aria-expanded', 'true')),
      onClose: () => toggles.forEach((b) => b.setAttribute('aria-expanded', 'false'))
    });
    toggles.forEach((b) => on(b, 'click', () => overlay.open(b)));
    $$('[data-menu-close]', drawer).forEach((b) => on(b, 'click', overlay.close));
    on(drawer, 'click', (e) => { if (e.target.closest('a[href^="#"]')) overlay.close(); });
    const mq = window.matchMedia('(min-width: 1100px)');
    on(mq, 'change', () => { if (mq.matches) overlay.close(); });
  };

  /* Search modal + predictive search (Section Rendering API) */
  const initSearch = () => {
    const modal = $('[data-search-modal]');
    if (!modal) return;
    const input = $('[data-predictive-input]', modal);
    const results = $('[data-predictive-results]', modal);
    const overlay = createOverlay(modal, { initialFocus: '[data-predictive-input]' });
    $$('[data-search-open]').forEach((b) => on(b, 'click', (e) => { e.preventDefault(); overlay.open(b); }));
    $$('[data-search-close]', modal).forEach((b) => on(b, 'click', overlay.close));

    if (!input || !results || !routes.predictiveSearch) return;
    let controller;
    const search = debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,article,page,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body'
      });
      try {
        const res = await fetch(`${routes.predictiveSearch}?${params}`, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const inner = $('#predictive-search-results', doc);
        results.innerHTML = inner ? inner.outerHTML : '';
        input.setAttribute('aria-expanded', inner ? 'true' : 'false');
        track('search_suggest', { query: q });
      } catch (err) {
        if (err.name !== 'AbortError') results.innerHTML = '';
      }
    }, 280);
    on(input, 'input', search);
  };

  /* ------------------------------------------------------------------ *
   * Cart: AJAX add / change with bundled section rendering.
   * ------------------------------------------------------------------ */
  const Cart = (() => {
    const drawerEl = $('[data-cart-drawer]');
    const useDrawer = EB.settings && EB.settings.cartType === 'drawer' && !!drawerEl;
    let overlay = null;

    const sectionIds = () => {
      const ids = [];
      if (drawerEl) ids.push('cart-drawer');
      const page = $('[data-cart-page]');
      if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
      return ids;
    };

    const setError = (msg) => {
      $$('[data-cart-error]').forEach((el) => {
        el.textContent = msg || '';
        el.hidden = !msg;
      });
    };

    const updateCount = (count) => {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        el.classList.remove('is-bump');
        void el.offsetWidth; // restart animation
        if (count > 0) el.classList.add('is-bump');
      });
    };

    const renderSections = (sections) => {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-drawer' && drawerEl) {
          const fresh = $('[data-cart-drawer]', doc);
          if (!fresh) return;
          const panel = $('.drawer__panel', drawerEl);
          const freshPanel = $('.drawer__panel', fresh);
          if (panel && freshPanel) panel.innerHTML = freshPanel.innerHTML;
          bindCartContainer(drawerEl);
        } else {
          const target = document.getElementById(`shopify-section-${id}`);
          const fresh = $(`#shopify-section-${id}`, doc) || doc.body;
          if (target && fresh) {
            target.innerHTML = fresh.innerHTML;
            const page = $('[data-cart-page]', target);
            if (page) bindCartContainer(page);
          }
        }
      });
    };

    const refreshCount = async () => {
      try {
        const res = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        updateCount(cart.item_count);
        const label = $('[data-cart-count-label]');
        if (label) label.textContent = String(cart.item_count);
        return cart;
      } catch (e) { return null; }
    };

    const open = (trigger) => {
      if (!useDrawer) { window.location.href = routes.cart; return; }
      if (!overlay) {
        overlay = createOverlay(drawerEl);
      }
      overlay.open(trigger);
      track('cart_drawer_open');
    };
    const close = () => overlay && overlay.close();

    const add = async (items, trigger) => {
      setError('');
      const body = { items, sections: sectionIds().join(','), sections_url: window.location.pathname };
      const res = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        const msg = data.description || data.message || strings.cartError;
        throw new Error(typeof msg === 'string' ? msg : strings.cartError);
      }
      renderSections(data.sections);
      await refreshCount();
      document.dispatchEvent(new CustomEvent('eb:cart:added', { detail: data }));
      if (useDrawer) open(trigger);
      else toast(strings.added);
      return data;
    };

    const change = async (line, quantity, lineEl) => {
      setError('');
      if (lineEl) lineEl.classList.add('is-updating');
      try {
        const res = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: sectionIds().join(','), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
        renderSections(data.sections);
        updateCount(data.item_count);
        if (quantity === 0) track('cart_line_removed', { line });
        return data;
      } catch (err) {
        setError(err.message || strings.cartError);
        if (lineEl) lineEl.classList.remove('is-updating');
        return null;
      }
    };

    const saveNote = debounce(async (note) => {
      try {
        await fetch(`${routes.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note })
        });
      } catch (e) { /* noop: note also saves on checkout submit */ }
    }, 500);

    const bindCartContainer = (root) => {
      if (!root) return;
      $$('[data-line-qty]', root).forEach((input) => {
        const commit = () => {
          const qty = Math.max(0, parseInt(input.value, 10) || 0);
          change(parseInt(input.dataset.lineQty, 10), qty, input.closest('[data-cart-line]'));
        };
        on(input, 'change', commit);
      });
      $$('[data-line-remove]', root).forEach((a) => {
        on(a, 'click', (e) => {
          e.preventDefault();
          change(parseInt(a.dataset.lineRemove, 10), 0, a.closest('[data-cart-line]'));
        });
      });
      $$('[data-cart-note]', root).forEach((ta) => on(ta, 'input', () => saveNote(ta.value)));
      $$('[data-cart-close]', root).forEach((b) => on(b, 'click', close));
      bindQty(root);
    };

    const init = () => {
      if (drawerEl) bindCartContainer(drawerEl);
      bindCartContainer($('[data-cart-page]'));
      if (useDrawer) {
        $$('[data-cart-open]').forEach((a) => on(a, 'click', (e) => {
          if (window.location.pathname === routes.cart) return;
          e.preventDefault();
          open(a);
        }));
      }
      // Restore count when returning via bfcache
      on(window, 'pageshow', (e) => { if (e.persisted) refreshCount(); });
    };

    return { init, add, change, open, close, refreshCount };
  })();
  EB.cart = Cart;

  /* Quantity steppers (product, guest picker, cart lines) */
  function bindQty(root = document) {
    $$('[data-qty]', root).forEach((wrap) => {
      if (wrap.dataset.qtyBound) return;
      wrap.dataset.qtyBound = '1';
      const input = $('input', wrap);
      if (!input) return;
      const step = (delta) => {
        const min = input.min !== '' ? parseInt(input.min, 10) : 0;
        const max = input.max !== '' ? parseInt(input.max, 10) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + delta));
        if (String(next) === input.value) return;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      on($('[data-qty-minus]', wrap), 'click', () => step(-1));
      on($('[data-qty-plus]', wrap), 'click', () => step(1));
    });
  }

  /* ------------------------------------------------------------------ *
   * Product: variant picker, guest picker, add to booking, sticky bar.
   * ------------------------------------------------------------------ */
  const initProduct = (section) => {
    if (!section || section.dataset.bound) return;
    section.dataset.bound = '1';
    const jsonEl = $('[data-product-json]', section);
    const form = $('[data-product-form]', section);
    if (!jsonEl || !form) return;
    let product;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }

    const picker = $('[data-variant-picker]', section);
    const guestPos = picker ? parseInt(picker.dataset.guestPosition || '0', 10) : 0;
    const guestPicker = $('[data-guest-picker]', section);
    const idInput = $('[data-variant-id]', form);
    const addBtn = $('[data-add-button]', form);
    const addLabel = $('[data-add-label]', form);
    const errorEl = $('[data-form-error]', form);
    const defaultLabel = addLabel ? addLabel.textContent.trim() : strings.addToCart;
    const sectionId = section.dataset.sectionId;
    const productUrl = section.dataset.productUrl;
    if (guestPicker) form.classList.add('has-guest-picker');

    const selectedOptions = () => {
      const opts = product.options.map((_, i) => {
        const checked = $(`input[data-option-position="${i + 1}"]:checked`, section);
        return checked ? checked.value : null;
      });
      return opts;
    };

    const findVariant = (opts) => product.variants.find((v) => v.options.every((val, i) => opts[i] === null || opts[i] === val));

    const setButton = (variant) => {
      if (!addBtn || !addLabel) return;
      let label = defaultLabel;
      let disabled = false;
      if (!variant) { label = strings.unavailable; disabled = true; }
      else if (!variant.available && !guestPicker) { label = strings.soldOut; disabled = true; }
      addBtn.disabled = disabled;
      addLabel.textContent = label;
    };

    const refreshPrice = async (variant) => {
      if (!variant || !sectionId) return;
      try {
        const res = await fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`);
        const doc = parseHTML(await res.text());
        [['[data-price-wrap]'], ['[data-sticky-price]']].forEach(([sel]) => {
          const fresh = $(sel, doc);
          const current = $(sel, section);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        // Guest row prices depend on the other options (e.g. Style)
        $$('[data-guest-row]', doc).forEach((row) => {
          const local = $(`[data-guest-row][data-guest-value="${CSS.escape(row.dataset.guestValue)}"] [data-guest-price]`, section);
          const fresh = $('[data-guest-price]', row);
          if (local && fresh) local.innerHTML = fresh.innerHTML;
        });
      } catch (e) { /* keep server-rendered price */ }
    };

    const markAvailability = () => {
      if (!picker) return;
      const current = selectedOptions();
      product.options.forEach((_, i) => {
        if (i + 1 === guestPos) return;
        $$(`input[data-option-position="${i + 1}"]`, section).forEach((input) => {
          const opts = current.slice();
          opts[i] = input.value;
          if (guestPos) opts[guestPos - 1] = null;
          const exists = product.variants.some((v) => v.available && v.options.every((val, j) => opts[j] === null || opts[j] === val));
          input.closest('.pill').classList.toggle('is-unavailable', !exists);
        });
      });
    };

    const guestItems = () => {
      if (!guestPicker) return [];
      const base = selectedOptions();
      return $$('[data-guest-row]', guestPicker).map((row) => {
        const qty = Math.max(0, parseInt($('[data-guest-qty]', row).value, 10) || 0);
        const opts = base.slice();
        opts[guestPos - 1] = row.dataset.guestValue;
        const variant = product.variants.find((v) => v.options.every((val, i) => opts[i] === val));
        row.classList.toggle('is-unavailable', !variant || !variant.available);
        const qtyInput = $('[data-guest-qty]', row);
        if (!variant || !variant.available) { qtyInput.value = 0; }
        return { row, variant, qty: (!variant || !variant.available) ? 0 : qty };
      });
    };

    const updateGuestTotal = () => {
      const totalEl = $('[data-guest-total]', guestPicker);
      if (!totalEl) return;
      const items = guestItems();
      const count = items.reduce((n, it) => n + it.qty, 0);
      const cents = items.reduce((n, it) => n + (it.variant ? it.variant.price * it.qty : 0), 0);
      totalEl.textContent = count > 0
        ? (strings.guestTotal || '[count] · [total]').replace('[count]', count).replace('[total]', formatMoney(cents))
        : (strings.guestNone || '');
      if (addBtn) addBtn.disabled = count === 0;
    };

    const onOptionChange = () => {
      let opts = selectedOptions();
      if (guestPos) {
        // Use the first selected traveler type (or first value) to resolve a representative variant
        const firstRow = guestPicker ? $$('[data-guest-row]', guestPicker).find((r) => parseInt($('[data-guest-qty]', r).value, 10) > 0) : null;
        opts[guestPos - 1] = firstRow ? firstRow.dataset.guestValue : product.options && product.variants[0].options[guestPos - 1];
      }
      const variant = findVariant(opts);
      if (variant && idInput) idInput.value = variant.id;
      setButton(variant);
      markAvailability();
      if (variant) {
        refreshPrice(variant);
        if (!EB.settings.designMode) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }
      if (guestPicker) updateGuestTotal();
    };

    $$('input[data-option-position]', section).forEach((input) => on(input, 'change', onOptionChange));
    if (guestPicker) {
      $$('[data-guest-qty]', guestPicker).forEach((input) => on(input, 'change', updateGuestTotal));
      updateGuestTotal();
    }
    markAvailability();
    bindQty(section);

    on(form, 'submit', async (e) => {
      e.preventDefault();
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      if (!form.reportValidity()) return;

      const fd = new FormData(form);
      const properties = {};
      fd.forEach((value, key) => {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && value !== '') properties[m[1]] = value;
      });

      let items;
      if (guestPicker) {
        items = guestItems().filter((it) => it.qty > 0 && it.variant).map((it) => ({ id: it.variant.id, quantity: it.qty, properties }));
        if (!items.length) {
          if (errorEl) { errorEl.textContent = strings.guestNone; errorEl.hidden = false; }
          return;
        }
      } else {
        items = [{ id: parseInt(fd.get('id'), 10), quantity: Math.max(1, parseInt(fd.get('quantity'), 10) || 1), properties }];
      }

      addBtn.classList.add('is-loading');
      addBtn.setAttribute('aria-busy', 'true');
      try {
        await Cart.add(items, addBtn);
        track('booking_added', {
          product_id: product.id,
          product_title: product.title,
          items: items.map((i) => ({ variant_id: i.id, quantity: i.quantity }))
        });
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message || strings.cartError; errorEl.hidden = false; }
      } finally {
        addBtn.classList.remove('is-loading');
        addBtn.removeAttribute('aria-busy');
      }
    });

    // Sticky mobile booking bar: visible while the booking card is off-screen.
    const sticky = $('[data-sticky-book]', section);
    const booking = $('[data-booking]', section);
    if (sticky && booking && 'IntersectionObserver' in window) {
      sticky.hidden = false;
      let bookingVisible = true;
      let pastHead = false;
      const sync = () => {
        const show = !bookingVisible && pastHead;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        $$('a, button', sticky).forEach((el) => { el.tabIndex = show ? 0 : -1; });
        document.body.classList.toggle('has-sticky-book', show);
      };
      new IntersectionObserver(([entry]) => { bookingVisible = entry.isIntersecting; sync(); }).observe(booking);
      const head = $('.pdp__head', section);
      if (head) new IntersectionObserver(([entry]) => { pastHead = !entry.isIntersecting && entry.boundingClientRect.top < 0; sync(); }).observe(head);
      on($('[data-sticky-book-btn]', sticky), 'click', (e) => {
        e.preventDefault();
        booking.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
        const first = $(FOCUSABLE, booking);
        if (first) setTimeout(() => first.focus({ preventScroll: true }), reducedMotion.matches ? 0 : 600);
      });
    }

    // Collapsible long description
    const desc = $('[data-collapsible-desc]', section);
    if (desc && desc.scrollHeight > 560) {
      desc.classList.add('is-collapsed');
      desc.id = desc.id || `Desc-${sectionId}`;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn--secondary btn--small xdetails__toggle';
      btn.setAttribute('aria-controls', desc.id);
      btn.setAttribute('aria-expanded', 'false');
      btn.innerHTML = `<span>${strings.readMore}</span>`;
      desc.after(btn);
      on(btn, 'click', () => {
        const collapsed = desc.classList.toggle('is-collapsed');
        btn.setAttribute('aria-expanded', String(!collapsed));
        btn.firstChild.textContent = collapsed ? strings.readMore : strings.readLess;
      });
    }

    track('experience_viewed', { product_id: product.id, product_title: product.title });
  };

  /* Gallery: mobile counter + full-screen viewer */
  const initGallery = (root) => {
    $$('[data-gallery]', root).forEach((gallery) => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = '1';
      const track_ = $('[data-gallery-track]', gallery);
      const index = $('[data-gallery-index]', gallery);
      if (track_ && index) {
        on(track_, 'scroll', debounce(() => {
          const items = $$('.gallery__item', track_);
          const w = items[0] ? items[0].getBoundingClientRect().width : 1;
          index.textContent = Math.min(items.length, Math.round(track_.scrollLeft / w) + 1);
        }, 60), { passive: true });
      }
      const viewer = $('[data-viewer]', gallery);
      if (!viewer) return;
      const vtrack = $('[data-viewer-track]', viewer);
      const slides = $$('[data-viewer-slide]', viewer);
      const count = $('[data-viewer-count]', viewer);
      const overlay = createOverlay(viewer, {
        onClose: () => $$('video', viewer).forEach((v) => v.pause())
      });
      const current = () => Math.round(vtrack.scrollLeft / (vtrack.clientWidth || 1));
      const go = (i, smooth = true) => {
        const n = (i + slides.length) % slides.length;
        vtrack.scrollTo({ left: n * vtrack.clientWidth, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto' });
        if (count) count.textContent = `${n + 1} / ${slides.length}`;
      };
      $$('[data-gallery-open]', gallery).forEach((btn) => on(btn, 'click', () => {
        overlay.open(btn);
        requestAnimationFrame(() => go(parseInt(btn.dataset.galleryOpen, 10) || 0, false));
      }));
      on($('[data-viewer-close]', viewer), 'click', overlay.close);
      on($('[data-viewer-prev]', viewer), 'click', () => go(current() - 1));
      on($('[data-viewer-next]', viewer), 'click', () => go(current() + 1));
      on(vtrack, 'scroll', debounce(() => { if (count) count.textContent = `${current() + 1} / ${slides.length}`; }, 60), { passive: true });
      on(viewer, 'keydown', (e) => {
        if (e.key === 'ArrowRight') go(current() + 1);
        if (e.key === 'ArrowLeft') go(current() - 1);
      });
    });
  };

  /* Rails (horizontal scrollers) with progress + prev/next */
  const initRails = (root = document) => {
    $$('[data-rail]', root).forEach((rail) => {
      if (rail.dataset.bound) return;
      rail.dataset.bound = '1';
      const trackEl = $('[data-rail-track]', rail);
      const prev = $('[data-rail-prev]', rail);
      const next = $('[data-rail-next]', rail);
      const progress = $('[data-rail-progress]', rail);
      if (!trackEl) return;
      const update = () => {
        const max = trackEl.scrollWidth - trackEl.clientWidth;
        const ratio = max > 0 ? trackEl.scrollLeft / max : 1;
        const visible = trackEl.clientWidth / trackEl.scrollWidth;
        if (progress) progress.parentElement.style.setProperty('--progress', Math.min(1, visible + ratio * (1 - visible)).toFixed(3));
        if (prev) prev.disabled = trackEl.scrollLeft <= 2;
        if (next) next.disabled = trackEl.scrollLeft >= max - 2;
        const controls = prev && prev.parentElement;
        if (controls) controls.hidden = max <= 2;
      };
      const page = (dir) => {
        const item = $('.rail__item', trackEl);
        const w = item ? item.getBoundingClientRect().width : trackEl.clientWidth * 0.8;
        trackEl.scrollBy({ left: dir * w * Math.max(1, Math.floor(trackEl.clientWidth / w)), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };
      on(prev, 'click', () => page(-1));
      on(next, 'click', () => page(1));
      on(trackEl, 'scroll', () => requestAnimationFrame(update), { passive: true });
      on(window, 'resize', debounce(update, 150));
      update();
    });
  };

  /* Product recommendations (Section Rendering API) */
  const initRecommendations = (root = document) => {
    $$('[data-recommendations]', root).forEach(async (el) => {
      if (el.dataset.bound || !el.dataset.url) return;
      el.dataset.bound = '1';
      const load = async () => {
        try {
          const res = await fetch(el.dataset.url);
          const doc = parseHTML(await res.text());
          const fresh = $('[data-recommendations]', doc);
          if (fresh && fresh.innerHTML.trim()) {
            el.innerHTML = fresh.innerHTML;
            el.hidden = false;
            initRails(el);
            document.dispatchEvent(new CustomEvent('eb:content', { detail: { root: el } }));
          }
        } catch (e) { /* keep fallback content */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((en) => en.isIntersecting)) { io.disconnect(); load(); }
        }, { rootMargin: '600px 0px' });
        io.observe(el);
      } else load();
    });
  };

  /* Collection / search filters: AJAX via Section Rendering API + history */
  const initFacets = () => {
    const sectionEl = $('[data-collection-section]');
    const form = $('[data-facets-form]');
    if (!sectionEl || !form) return;
    const sectionId = sectionEl.dataset.sectionId;
    const container = $('#ProductGridContainer') || sectionEl;
    const facets = $('[data-facets]');
    let overlay = null;
    const drawer = $('[data-facets-drawer]');
    const desktop = window.matchMedia('(min-width: 1200px)');

    const openDrawer = (btn) => {
      if (!drawer || !facets) return;
      facets.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      lockScroll();
      const release = trapFocus(drawer, btn);
      const first = $(FOCUSABLE, drawer);
      if (first) first.focus({ preventScroll: true });
      overlay = () => {
        facets.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        unlockScroll();
        release();
        overlay = null;
      };
    };
    const closeDrawer = () => overlay && overlay();

    const render = async (url, push = true) => {
      const results = $('[data-results]', container);
      if (results) results.classList.add('is-loading');
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);
      try {
        const res = await fetch(fetchUrl.toString());
        const doc = parseHTML(await res.text());
        const fresh = $('#ProductGridContainer', doc);
        if (!fresh) throw new Error('no grid');
        const wasOpen = facets && facets.classList.contains('is-open');
        // Preserve which <details> groups are open
        const openGroups = $$('.facet', container).map((d) => d.open);
        closeDrawer();
        container.innerHTML = fresh.innerHTML;
        $$('.facet', container).forEach((d, i) => { if (openGroups[i] !== undefined) d.open = openGroups[i]; });
        if (push) window.history.pushState({ facets: true }, '', url);
        initFacets();
        document.dispatchEvent(new CustomEvent('eb:content', { detail: { root: container } }));
        if (wasOpen && !desktop.matches) {
          const btn = $('[data-facets-open]');
          if (btn) openDrawer(btn);
        }
        const count = $('[data-results-count]', container);
        track('collection_filtered', { url, results: count ? count.textContent.trim() : '' });
      } catch (e) {
        window.location.href = url;
      }
    };

    const urlFromForm = () => {
      const fd = new FormData($('[data-facets-form]'));
      const params = new URLSearchParams();
      fd.forEach((v, k) => { if (v !== '') params.append(k, v); });
      const action = $('[data-facets-form]').getAttribute('action') || window.location.pathname;
      return `${action}?${params.toString()}`;
    };

    const onChange = debounce(() => render(urlFromForm()), 350);

    // Bind (idempotent per render because container content is replaced)
    const f = $('[data-facets-form]', container) || form;
    on(f, 'change', (e) => {
      if (e.target.matches('[data-sort]') || desktop.matches || e.target.type === 'number') onChange();
      else if (e.target.type === 'checkbox' || e.target.type === 'range') onChange();
    });
    // Price slider <-> number inputs
    $$('[data-price-range]', container).forEach((wrap) => {
      const max = parseFloat(wrap.dataset.max) || 0;
      const sl = { min: $('[data-price-slider="min"]', wrap), max: $('[data-price-slider="max"]', wrap) };
      const inp = { min: $('[data-price-input="min"]', wrap), max: $('[data-price-input="max"]', wrap) };
      const fill = $('[data-price-fill]', wrap);
      const paint = () => {
        if (!fill || !max) return;
        fill.style.setProperty('--from', `${(sl.min.value / max) * 100}%`);
        fill.style.setProperty('--to', `${(sl.max.value / max) * 100}%`);
      };
      ['min', 'max'].forEach((k) => {
        if (!sl[k] || !inp[k]) return;
        on(sl[k], 'input', () => {
          if (parseFloat(sl.min.value) > parseFloat(sl.max.value)) sl[k].value = k === 'min' ? sl.max.value : sl.min.value;
          const atEdge = (k === 'min' && +sl[k].value === 0) || (k === 'max' && +sl[k].value === max);
          inp[k].value = atEdge ? '' : sl[k].value;
          paint();
        });
        on(inp[k], 'input', () => { sl[k].value = inp[k].value === '' ? (k === 'min' ? 0 : max) : inp[k].value; paint(); });
      });
    });
    // "Show all" for long filter lists
    $$('[data-facet-more]', container).forEach((b) => {
      b.hidden = false;
      const list = b.previousElementSibling;
      if (list) list.classList.add('is-collapsed');
      on(b, 'click', () => { if (list) list.classList.remove('is-collapsed'); b.remove(); });
    });
    on(f, 'submit', (e) => { e.preventDefault(); closeDrawer(); render(urlFromForm()); });
    $$('[data-facet-remove], [data-facets-clear]', container).forEach((a) => on(a, 'click', (e) => { e.preventDefault(); render(a.href); }));
    $$('[data-facets-open]', container).forEach((b) => on(b, 'click', () => openDrawer(b)));
    $$('[data-facets-close]', container).forEach((b) => on(b, 'click', closeDrawer));
    if (drawer) on(drawer, 'keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });
    if (facets) on(facets, 'click', (e) => { if (e.target === facets) closeDrawer(); });
    $$('.pagination a', container).forEach((a) => on(a, 'click', (e) => {
      e.preventDefault();
      render(a.href);
      sectionEl.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }));

    if (!window.__ebFacetsPop) {
      window.__ebFacetsPop = true;
      on(window, 'popstate', () => { if ($('[data-facets-form]')) render(window.location.href, false); });
    }
  };

  /* Article: table of contents from H2s + active section */
  const initTOC = () => {
    const toc = $('[data-toc]');
    const body = $('[data-article-body]');
    if (!toc || !body) return;
    const heads = $$('h2', body);
    if (heads.length < 3) return;
    const list = $('[data-toc-list]', toc);
    const slug = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const links = heads.map((h, i) => {
      if (!h.id) h.id = slug(h.textContent) || `section-${i + 1}`;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = h.textContent.trim();
      li.appendChild(a);
      list.appendChild(li);
      return a;
    });
    toc.hidden = false;
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          links.forEach((a) => {
            const active = a.getAttribute('href') === `#${en.target.id}`;
            a.classList.toggle('is-active', active);
            if (active) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
          });
        });
      }, { rootMargin: '-20% 0px -70% 0px' });
      heads.forEach((h) => io.observe(h));
    }
  };

  /* Copy link */
  const initCopy = () => {
    $$('[data-copy-link]').forEach((btn) => on(btn, 'click', async () => {
      const url = btn.dataset.copyLink || window.location.href;
      try {
        if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
          await navigator.share({ url, title: document.title });
        } else {
          await navigator.clipboard.writeText(url);
          toast(strings.copied);
        }
        track('share_click', { method: 'copy', url });
      } catch (e) { /* user cancelled */ }
    }));
  };

  /* Map facade: loads Google Maps only after an explicit click (privacy + performance) */
  const initMaps = (root = document) => {
    $$('[data-map-facade]', root).forEach((wrap) => {
      const btn = $('[data-map-load]', wrap);
      on(btn, 'click', () => {
        const iframe = document.createElement('iframe');
        iframe.src = wrap.dataset.mapSrc;
        iframe.loading = 'lazy';
        iframe.title = btn.textContent.trim();
        iframe.referrerPolicy = 'no-referrer-when-downgrade';
        wrap.innerHTML = '';
        wrap.appendChild(iframe);
        track('map_loaded');
      });
    });
  };

  /* Localization selectors auto-submit */
  const initLocalization = () => {
    $$('[data-localization-select]').forEach((sel) => on(sel, 'change', () => {
      const f = sel.closest('form');
      if (f) f.submit();
    }));
    const menus = $$('[data-locale-menu]');
    menus.forEach((m) => on(m, 'toggle', () => {
      if (m.open) menus.forEach((o) => { if (o !== m) o.open = false; });
    }));
    on(document, 'click', (e) => menus.forEach((m) => { if (m.open && !m.contains(e.target)) m.open = false; }));
    on(document, 'keydown', (e) => {
      if (e.key !== 'Escape') return;
      menus.forEach((m) => { if (m.open) { m.open = false; m.querySelector('summary').focus(); } });
    });
  };

  /* Hero video: load only on capable connections, honour reduced motion, expose pause */
  const initHeroVideo = (root = document) => {
    $$('[data-hero]', root).forEach((hero) => {
      const wrap = $('[data-hero-video]', hero);
      const toggle = $('[data-video-toggle]', hero);
      if (!wrap) return;
      const video = $('video', wrap);
      if (!video) return;
      const conn = navigator.connection || {};
      const saveData = conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '');
      const setPressed = (paused) => {
        if (!toggle) return;
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
      };
      if (saveData || reducedMotion.matches) {
        if (toggle) {
          setPressed(true);
          on(toggle, 'click', () => start(true));
        }
        return;
      }
      let started = false;
      function start(userInitiated) {
        if (!started) {
          started = true;
          if (video.dataset.src) video.src = video.dataset.src;
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          on(video, 'playing', () => wrap.classList.add('is-playing'), { once: true });
          if (toggle) {
            on(toggle, 'click', () => {
              if (video.paused) { video.play(); setPressed(false); }
              else { video.pause(); setPressed(true); }
            });
          }
        }
        const p = video.play();
        if (p && p.catch) p.catch(() => setPressed(true));
        setPressed(false);
        if (userInitiated) track('hero_video_play');
      }
      // Wait for idle so the poster/LCP image wins first.
      const kick = () => start(false);
      if ('requestIdleCallback' in window) requestIdleCallback(kick, { timeout: 2500 });
      else setTimeout(kick, 1200);
      // Pause when off-screen to save CPU/battery.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => {
          if (!started || (toggle && toggle.getAttribute('aria-pressed') === 'true')) return;
          if (en.isIntersecting) video.play().catch(() => {}); else video.pause();
        }).observe(hero);
      }
    });
  };

  /* Forms: prevent double submission, mark invalid fields, report success */
  const initForms = () => {
    $$('[data-lead-form], [data-newsletter-form]').forEach((f) => {
      on(f, 'submit', (e) => {
        if (!f.checkValidity()) {
          e.preventDefault();
          $$('input, select, textarea', f).forEach((el) => el.setAttribute('aria-invalid', String(!el.checkValidity())));
          const firstBad = $(':invalid', f);
          if (firstBad) firstBad.focus();
          f.reportValidity();
          return;
        }
        const btn = $('[type="submit"]', f);
        if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
        track(f.matches('[data-lead-form]') ? 'lead_form_submit_attempt' : 'newsletter_submit_attempt', { form: f.dataset.leadForm || 'newsletter' });
      });
    });
    trackSuccess();
    // Bring the success / error message into view after a native POST redirect.
    const msg = $('.form-success, [data-newsletter-form] .form-message, .lead-form .form-message--error');
    if (msg && /form_type|customer_posted|contact_posted/.test(window.location.search + window.location.hash)) {
      msg.scrollIntoView({ block: 'center' });
      if (msg.focus) msg.focus({ preventScroll: true });
    }
  };

  /* Brand story: swap the sticky image as each chapter reaches the middle of the viewport */
  const initStory = (root = document) => {
    $$('[data-story]', root).forEach((story) => {
      const imgs = $$('[data-story-img]', story);
      const chapters = $$('[data-story-chapter]', story);
      if (!imgs.length || !chapters.length || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const i = en.target.dataset.storyChapter;
          imgs.forEach((img) => img.classList.toggle('is-active', img.dataset.storyImg === i));
        });
      }, { rootMargin: '-45% 0px -45% 0px' });
      chapters.forEach((c) => io.observe(c));
    });
  };

  /* Custom "View" cursor on cards (desktop fine pointer only) */
  const initCursor = () => {
    const cursor = $('[data-view-cursor]');
    if (!cursor || reducedMotion.matches || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    let x = 0, y = 0, cx = 0, cy = 0, raf = null;
    const loop = () => {
      cx += (x - cx) * 0.2;
      cy += (y - cy) * 0.2;
      cursor.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
      raf = Math.abs(x - cx) + Math.abs(y - cy) > 0.3 ? requestAnimationFrame(loop) : null;
    };
    on(document, 'pointermove', (e) => {
      x = e.clientX; y = e.clientY;
      const active = !!e.target.closest('[data-cursor="view"]');
      cursor.classList.toggle('is-active', active);
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });
    on(document, 'pointerleave', () => cursor.classList.remove('is-active'));
  };

  /* Theme editor: re-init sections loaded or re-rendered by the editor */
  const initSection = (root) => {
    $$('[data-product-section]', root).forEach(initProduct);
    initGallery(root);
    initRails(root);
    initRecommendations(root);
    initMaps(root);
    initHeroVideo(root);
    initStory(root);
    bindQty(root);
  };

  if (EB.settings && EB.settings.designMode) {
    on(document, 'shopify:section:load', (e) => {
      initSection(e.target);
      if ($('[data-header]', e.target)) { initHeader(); initMenuDrawer(); initSearch(); }
      if ($('[data-facets-form]', e.target)) initFacets();
      document.dispatchEvent(new CustomEvent('eb:content', { detail: { root: e.target } }));
    });
    on(document, 'shopify:block:select', (e) => {
      const panel = e.target.closest('[data-stack-panel], .rail__item');
      if (panel) e.target.scrollIntoView({ block: 'nearest', inline: 'start' });
      const details = e.target.closest('details');
      if (details) details.open = true;
    });
  }

  /* Boot */
  const boot = () => {
    document.documentElement.classList.add('js');
    initHeader();
    initMenuDrawer();
    initSearch();
    Cart.init();
    initSection(document);
    initFacets();
    initTOC();
    initCopy();
    initLocalization();
    initForms();
    initCursor();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
